import { toPostmanCollection } from './toPostman';
import { toApibooCollection } from './toApiboo';
import { toOpenApi3 } from './toOpenApi';
import { toInsomniaExport } from './toInsomnia';
import { toBrunoCollection } from './toBruno';
import { createExportWarnings } from './warnings';

export const EXPORT_FORMATS = {
  apiboo: 'apiboo',
  postman: 'postman',
  insomnia: 'insomnia',
  bruno: 'bruno',
  openapi_yaml: 'openapi_yaml',
  openapi_json: 'openapi_json',
};

/**
 * @param {object} collection
 * @param {{ format: string }} options
 */
export function exportCollection(collection, options = {}) {
  const warnings = createExportWarnings();
  const format = options.format || EXPORT_FORMATS.postman;
  const safeName = (collection.name || 'collection').replace(/[^\w.-]+/g, '_');

  if (format === EXPORT_FORMATS.apiboo) {
    const data = toApibooCollection(collection, warnings);
    return {
      content: JSON.stringify(data, null, 2),
      filename: `${safeName}.apiboo.json`,
      mime: 'application/json',
      // Always [] — an Apiboo export with a warning would mean the native
      // format failed at the one thing it exists for. See toApiboo.js.
      warnings: warnings.list(),
    };
  }

  if (format === EXPORT_FORMATS.postman) {
    const data = toPostmanCollection(collection, warnings);
    return {
      content: JSON.stringify(data, null, 2),
      filename: `${safeName}.postman_collection.json`,
      mime: 'application/json',
      warnings: warnings.list(),
    };
  }

  if (format === EXPORT_FORMATS.insomnia) {
    const data = toInsomniaExport(collection, warnings);
    return {
      content: JSON.stringify(data, null, 2),
      filename: `${safeName}.insomnia_collection.json`,
      mime: 'application/json',
      warnings: warnings.list(),
    };
  }

  if (format === EXPORT_FORMATS.bruno) {
    const { files } = toBrunoCollection(collection, warnings);
    return {
      files,
      // A readable concatenation for the modal preview; actual save writes `files`.
      content: files.map((f) => `// ${f.path}\n${f.content}`).join('\n\n'),
      filename: `${safeName}-bruno`,
      mime: 'text/plain',
      multiFile: true,
      warnings: warnings.list(),
    };
  }

  if (format === EXPORT_FORMATS.openapi_yaml) {
    const yamlText = toOpenApi3(collection, warnings, { asYaml: true });
    return {
      content: yamlText,
      filename: `${safeName}.openapi.yaml`,
      mime: 'application/x-yaml',
      warnings: warnings.list(),
    };
  }

  if (format === EXPORT_FORMATS.openapi_json) {
    const spec = toOpenApi3(collection, warnings, { asYaml: false });
    return {
      content: JSON.stringify(spec, null, 2),
      filename: `${safeName}.openapi.json`,
      mime: 'application/json',
      warnings: warnings.list(),
    };
  }

  throw new Error(`Unknown export format: ${format}`);
}

export { toPostmanCollection, toOpenApi3, toInsomniaExport, toBrunoCollection };
